import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { EllipsisIcon, PlusIcon } from 'lucide-react';

import type { ProjectLayoutType } from '@/api/media';
import { getMediaOptions } from '@/api/media';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ImageCarousel } from '@/components/ui/image-carousel';
import type { UploadMediaModalType } from '@/features/estate-media/components/upload-media-modal';
import {
  ACCEPTED_FLOORPLANS_INPUT_TYPE,
  uploadLayoutsSchema,
} from '@/features/estate-media/schema/upload-media';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import { useUploadFloorLayouts } from '@/features/project-media/api/upload-floor-layout';
import { useModal } from '../../../../components/modals/new/use-modal';
import { UploadMediaModal } from '../../../estate-media/components/upload-media-modal';

type FloorPlansCard = {
  data: ProjectLayoutType[][];
  projectId: string;
  canWriteMedia: boolean;
};

export const FloorPlansCard = ({ data = [], projectId, canWriteMedia }: FloorPlansCard) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modal = useModal();

  const images = data?.flat() || [];
  const amount = images.length;

  const queryClient = useQueryClient();

  const handleFloorPlansNavigate = () => {
    navigate({ to: '/projects/$projectId/media/floor-plans', params: { projectId } });
  };

  const { mutateAsync: uploadFloorLayouts } = useUploadFloorLayouts({
    mutationConfig: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getMediaOptions(projectId).queryKey });
      },
    },
  });

  const submitHandler = async (data: UploadMediaModalType) => {
    // Convert File[] to FileList
    const dataTransfer = new DataTransfer();
    data.files.forEach((file) => dataTransfer.items.add(file));
    await uploadFloorLayouts({ layouts: dataTransfer.files, projectId });
    modal.dismiss();
  };

  const handleOpenModal = () => {
    modal.open();
  };

  return (
    <>
      <Card className="flex h-full flex-col rounded-md">
        <div className="flex h-52 w-full items-center justify-center overflow-hidden">
          {amount === 0 ? (
            <EstateImage
              className="h-52 w-full object-cover"
              thumbnailId="default-floorplan-image"
            />
          ) : (
            <ImageCarousel imageClassName="h-52 w-full object-cover" images={images} />
          )}
        </div>

        <div className="flex w-full justify-between bg-white px-10 pb-14 pt-7 sm:flex-col">
          <div>
            <h3 className="text-md">{t('floorPlans')}</h3>
            <span className="text-base text-text-secondary">{amount}</span>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              className="w-full"
              variant="contained"
              size="md"
              onClick={handleFloorPlansNavigate}
            >
              <EllipsisIcon className="max-xl:block xl:hidden" />
              <p className="text-xs max-xl:hidden"> {t('seeAllBtn', { value: t('floorPlans') })}</p>
            </Button>
            {canWriteMedia ? (
              <Button className="w-full" variant="outline" onClick={handleOpenModal}>
                <PlusIcon className="max-xl:block" />
                <p className="max-xl:hidden">{t('add-new', { value: t('images') })}</p>
              </Button>
            ) : null}
          </div>
        </div>
      </Card>
      <UploadMediaModal
        modal={modal}
        submitHandler={submitHandler}
        acceptedType={ACCEPTED_FLOORPLANS_INPUT_TYPE}
        acceptedMediaSchema={uploadLayoutsSchema()}
      />
    </>
  );
};
